import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTrackUsage } from '../../../hooks/useTrackUsage';

const shortcuts = [
    { group: 'Playback', key: 'Tab', action: 'Play / Pause' },
    { group: 'Playback', key: 'Space', action: 'Play / Pause (while timeline focused)' },
    { group: 'Editing', key: 'Ctrl + Z', action: 'Undo last change' },
    { group: 'Editing', key: 'Ctrl + S', action: 'Save subtitles' },
    { group: 'Editing', key: 'Shift + Enter', action: 'Split subtitle at cursor (text field only)' },
    { group: 'Editing', key: 'Ctrl + M', action: 'Merge active row with next' },
    { group: 'Navigation', key: '↑ / ↓', action: 'Move to previous / next subtitle row' },
    { group: 'Navigation', key: 'Ctrl + B', action: 'Jump to next bookmarked subtitle' },
    { group: 'Navigation', key: 'Ctrl + F', action: 'Open Find & Replace' },
    { group: 'Timing', key: 'Ctrl + ←', action: 'Nudge active subtitle start −100ms' },
    { group: 'Timing', key: 'Ctrl + →', action: 'Nudge active subtitle end +100ms' },
    { group: 'Timeline', key: '← / → (while dragging block)', action: 'Nudge dragged block ±0.1s' },
    { group: 'Timeline', key: 'Backspace / Delete (while dragging)', action: 'Delete dragged subtitle' },
    { group: 'Timeline', key: 'Ctrl + Scroll', action: 'Zoom timeline in / out' },
    { group: 'Timeline', key: 'Scroll', action: 'Pan timeline left / right' },
];

const groupColors = {
    Playback: 'primary',
    Editing: 'secondary',
    Navigation: 'success',
    Timing: 'warning',
    Timeline: 'info',
};

/**
 * KeyboardShortcutsModal
 * Props: { open, onClose }
 */
export default function KeyboardShortcutsModal({ open, onClose }) {
    useTrackUsage('KeyboardShortcutsModal', 'dialog', 'subtitles', open);
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                    fontWeight: 700,
                }}
            >
                Keyboard Shortcuts
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' } }}>
                            <TableCell width={40}>Area</TableCell>
                            <TableCell width={180}>Shortcut</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shortcuts.map(({ group, key, action }) => (
                            <TableRow
                                key={key}
                                sx={{ '&:last-child td': { borderBottom: 0 }, verticalAlign: 'middle' }}
                            >
                                <TableCell>
                                    <Chip
                                        label={group}
                                        color={groupColors[group] || 'default'}
                                        size="small"
                                        sx={{ fontSize: '0.65rem', height: 18 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {key.split('+').map((k, i) => (
                                            <span key={i}>
                                                {i > 0 && (
                                                    <Typography
                                                        component="span"
                                                        sx={{ fontSize: '0.75rem', color: 'text.secondary', mx: 0.3 }}
                                                    >
                                                        +
                                                    </Typography>
                                                )}
                                                <Box
                                                    component="kbd"
                                                    sx={{
                                                        px: '5px',
                                                        py: '2px',
                                                        borderRadius: '3px',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        bgcolor: 'action.hover',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.74rem',
                                                        lineHeight: 1.4,
                                                        display: 'inline-block',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {k.trim()}
                                                </Box>
                                            </span>
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.82rem' }}>{action}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
        </Dialog>
    );
}
